/* Las fotos de una herramienta: lo que se puede decidir sin la base.
 *
 * P-348 (Nico, 08/10/2026): «Quiero agregar fotos de herramientas cuando
 * agrego una herramienta». Son OPTATIVAS: nunca se piden y no reemplazan al
 * dibujo, que sigue armando el tablero y la silueta
 * (`shared/dibujosHerramienta.js`). Se miran en la ficha de UNA herramienta.
 * Ver `supabase/migrations/20261009020000_herramientas_fotos_optativas.sql`.
 */

/* El bucket donde viven los archivos. Público para leer: una foto de una
   pinza no es un dato personal, y la URL se arma con la ruta. */
export const BUCKET_HERRAMIENTAS = "herramientas";

/* Cuántas fotos VIVAS puede tener una herramienta. Lo controla el que sube
   —acá y en el bot—, que es el que le puede avisar a la persona. */
export const TOPE_FOTOS = 12;

/* Antes de subir, el navegador la achica: una foto de teléfono son 4.000 px y
   8 MB, y para reconocer una amoladora alcanza con 1.600 px de lado mayor. */
export const LADO_MAYOR = 1600;
export const CALIDAD_JPEG = 0.82;

/* Las medidas achicadas, sin deformar. Una foto que ya entra no se agranda. */
export function medidaAchicada(ancho, alto, lado = LADO_MAYOR) {
  const a = Math.max(0, Math.round(Number(ancho) || 0));
  const h = Math.max(0, Math.round(Number(alto) || 0));
  if (!a || !h) return { ancho: a, alto: h };
  const mayor = Math.max(a, h);
  if (mayor <= lado) return { ancho: a, alto: h };
  const k = lado / mayor;
  return {
    ancho: Math.max(1, Math.round(a * k)),
    alto: Math.max(1, Math.round(h * k)),
  };
}

/* La extensión que corresponde a un tipo de imagen; si no se sabe, la del
   nombre del archivo; y si tampoco, jpg. */
const POR_MIME = {
  "image/jpeg": "jpg", "image/jpg": "jpg", "image/png": "png",
  "image/webp": "webp", "image/gif": "gif", "image/heic": "heic",
  "image/heif": "heif",
};
export function extensionDe(mime, nombre = "") {
  const m = String(mime || "").toLowerCase();
  if (POR_MIME[m]) return POR_MIME[m];
  const delNombre = /\.([a-z0-9]{1,5})$/i.exec(String(nombre || ""));
  return delNombre ? delNombre[1].toLowerCase() : "jpg";
}

/* `fotos/<herramienta_id>/<algo único>.<ext>`: la forma que acordaron el Hub
   y el bot. `unico` lo arma quien llama (hora + algo al azar) para que dos
   fotos elegidas en el mismo milisegundo no se pisen. */
export function rutaDeFoto(herramientaId, ext, unico) {
  const e = String(ext || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
  return `fotos/${herramientaId}/${unico}.${e}`;
}

export const nombreUnico = (ahora = Date.now(), azar = Math.random()) =>
  `${ahora}-${Math.floor(azar * 1e9).toString(36)}`;

/* ¿Cuántas de las elegidas entran? Las que sobran no se suben y se dice. */
export function cuantasEntran(vivas, elegidas, tope = TOPE_FOTOS) {
  const lugar = Math.max(0, tope - Math.max(0, Number(vivas) || 0));
  const n = Math.max(0, Number(elegidas) || 0);
  return { entran: Math.min(lugar, n), sobran: Math.max(0, n - lugar), lugar };
}

/* Lo que se le dice a la persona cuando eligió más de las que entran. Sin
   explicar dónde se guardan: qué pasó y qué hacer. */
export function avisoDeTope(sobran, tope = TOPE_FOTOS) {
  if (!sobran) return "";
  const cuantas = sobran === 1 ? "Una foto quedó afuera" : `${sobran} fotos quedaron afuera`;
  return `${cuantas}: una herramienta tiene hasta ${tope}. ` +
    "Si querés sumar otra, quitá alguna de las que ya tiene.";
}

/* ¿Es una imagen? El input ya filtra con `accept="image/*"`, pero en la compu
   se puede elegir «Todos los archivos». */
export const esImagen = (archivo) =>
  /^image\//i.test(String(archivo?.type || "")) ||
  /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(String(archivo?.name || ""));
